import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import GameSignals from '../../signals/GameSignals'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { BaseContainer } from '@base/base/BaseContainer'
import gsap from 'gsap'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import * as PIXI from 'pixi.js'
import {
    isPortrait,
    isFreeSpins,
    getBaseBackgroundAnimation,
    getFreeSpinsBackgroundAnimation,
    saveBaseTransforms,
    applyFreeSpinsLayout,
    restoreBaseLayout,
    ensureFreeSpinsCounterTexts,
    updateFreeSpinsCounterTexts,
    ensureFreeSpinsTopInfoTexts,
    clearFreeSpinsTopTweens,
    updateFreeSpinsTopInfoTexts,
    FS_COUNTER_VALUE_CHILD_NAME,
    onFsWinFrameTopShowFreeSpins,
    handleFsTopValuesFlow,
    handleFsTopMultiplierCollectFly,
    handleFsWinFrameTopOnBaseGame,
} from '@/managers/ui/BackgroundManager'
export const BACKGROUND_COMPONENT: IComponent = {
    name: ContainersNames.BACKGROUND_CONTAINER,
    type: AssetType.CONTAINER,
    visible: false,
    events: [
        {
            onGameSignalsTriggered: [GameSignals.splashScreenButtonClicked],
            event: ComponentEvent.CUSTOM,
            callback: function (componentData: IComponent, container: BaseContainer) {
                container.alpha = 0
                container.visible = true
                gsap.to(container, {
                    duration: 2,
                    alpha: 1,
                })
            },
        },
    ],

    items: [
        {
            name: 'Static Base Background Landscape',
            type: AssetType.SPRITE,
            visible: false,
            src: 'bg_landscape.png',
            events: [
                {
                    onGameSignalsTriggered: [
                        GameSignals.showBaseGameBackground,
                        GameSignals.completeSplashScreenState,
                        ModuleSignals.gameResized,
                    ],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        data.component.componentElement.visible = !isPortrait() && !isFreeSpins()
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
                    event: ComponentEvent.HIDE,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0,
                    y: 0,
                },
            ],
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.75,
                    y: 0.75,
                },
            ],
        },
        {
            name: 'Static Base Background Portrait',
            type: AssetType.SPRITE,
            visible: false,
            src: 'bg_portrait.png',
            events: [
                {
                    onGameSignalsTriggered: [
                        GameSignals.showBaseGameBackground,
                        GameSignals.completeSplashScreenState,
                        ModuleSignals.gameResized,
                    ],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        data.component.componentElement.visible = isPortrait() && !isFreeSpins()
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
                    event: ComponentEvent.HIDE,
                },
            ],
            position: [
                {
                    orientation: Orientation.PORTRAIT,
                    x: -2.4,
                    y: 4,
                },
            ],
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.75,
                    y: 0.75,
                },
            ],
        },
        {
            name: 'Static Free Spins Background Landscape',
            type: AssetType.SPRITE,
            visible: false,
            src: 'bg_fs_landscape.png',
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground, ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        data.component.componentElement.visible = !isPortrait() && isFreeSpins()
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground, GameSignals.completeSplashScreenState],
                    event: ComponentEvent.HIDE,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0,
                    y: 0,
                },
            ],
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.75,
                    y: 0.75,
                },
            ],
        },
        {
            name: 'Static Free Spins Background Portrait',
            type: AssetType.SPRITE,
            visible: false,
            src: 'bg_fs_portrait.png',
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground, ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        data.component.componentElement.visible = isPortrait() && isFreeSpins()
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground, GameSignals.completeSplashScreenState],
                    event: ComponentEvent.HIDE,
                },
            ],
            position: [
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0,
                    y: -2,
                },
            ],
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
        },
        {
            name: 'Base background',
            type: AssetType.SPINE,
            src: 'baseBackgroundAnimation',
            visible: true,
            animationSettings: {
                default: {
                    animNames: ['MG_L'],
                    loops: [true],
                    track: 0,
                    timeScales: [1.35],
                },
            },
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        data.component.componentElement.stop()
                        data.component.componentElement.visible = false
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        data.component.playAnimation({
                            animNames: [getBaseBackgroundAnimation()],
                            loops: [true],
                            track: 0,
                            timeScales: [1.35],
                        })
                        data.component.componentElement.visible = true
                    },
                },
                {
                    onGameSignalsTriggered: [ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (!data.component.componentElement.visible) return
                        if (isFreeSpins()) return

                        data.component.playAnimation({
                            animNames: [getBaseBackgroundAnimation()],
                            loops: [true],
                            track: 0,
                            timeScales: [1.35],
                        })
                    },
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1.525,
                    y: 1.55,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 954,
                    y: 532,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 602.5,
                    y: 417,
                },
            ],
        },
        {
            name: 'Bonus background',
            type: AssetType.SPINE,
            src: 'baseBackgroundAnimation',
            visible: false,
            animationSettings: {
                default: {
                    animNames: ['FS_L'],
                    loops: [true],
                    track: 0,
                    timeScales: [1.35],
                },
            },
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        data.component.playAnimation({
                            animNames: [getFreeSpinsBackgroundAnimation()],
                            loops: [true],
                            track: 0,
                            timeScales: [1.35],
                        })
                        data.component.componentElement.visible = true
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        data.component.componentElement.stop()
                        data.component.componentElement.visible = false
                    },
                },
                {
                    onGameSignalsTriggered: [ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (!data.component.componentElement.visible) return
                        if (!isFreeSpins()) return

                        data.component.playAnimation({
                            animNames: [getFreeSpinsBackgroundAnimation()],
                            loops: [true],
                            track: 0,
                            timeScales: [1.35],
                        })
                    },
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1,
                    y: 1,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 491,
                    y: 359.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 540,
                    y: 693.5,
                },
            ],
        },
        {
            name: 'Machine Frame',
            type: AssetType.SPRITE,
            visible: false,
            src: `REEL.png`,
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    event: ComponentEvent.CUSTOM,
                    callback: function (componentData: IComponent, container: BaseContainer) {
                        saveBaseTransforms(container)
                        restoreBaseLayout(container)
                        container.alpha = 0
                        container.visible = true
                        gsap.to(container, {
                            duration: 2,
                            alpha: 1,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.bonusStart],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        applyFreeSpinsLayout(data.component.componentElement)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        restoreBaseLayout(data.component.componentElement)
                    },
                },
                {
                    onGameSignalsTriggered: [ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (isFreeSpins()) {
                            applyFreeSpinsLayout(data.component.componentElement)
                            return
                        }

                        restoreBaseLayout(data.component.componentElement)
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 448.5,
                    y: -58,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: -91,
                    y: 294.5,
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.69,
                    y: 0.69,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.72,
                    y: 0.72,
                },
            ],
        },
        {
            name: 'FS Win Frame Top',
            type: AssetType.SPRITE,
            visible: false,
            src: `FSWinFramepng.png`,
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground, ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        onFsWinFrameTopShowFreeSpins(data.component.componentElement as PIXI.Container)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.completeTransitionAnimation],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (isPortrait() || !isFreeSpins()) {
                            return
                        }

                        const element = data.component.componentElement
                        ensureFreeSpinsTopInfoTexts(element)
                        clearFreeSpinsTopTweens()
                        updateFreeSpinsTopInfoTexts(element)
                        gsap.killTweensOf(element)
                        element.alpha = 0
                        element.visible = true
                        gsap.to(element, {
                            duration: 0.35,
                            alpha: 1,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.fsTopValuesFlow],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        handleFsTopValuesFlow(data)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.fsTopMultiplierCollectFly],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        handleFsTopMultiplierCollectFly(data)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        handleFsWinFrameTopOnBaseGame(data.component.componentElement as PIXI.Container)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.bonusStop],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        gsap.killTweensOf(element)

                        if (!element.visible) {
                            return
                        }

                        gsap.to(element, {
                            duration: 0.35,
                            alpha: 0,
                            onComplete: () => {
                                element.visible = false
                            },
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    event: ComponentEvent.HIDE,
                },
            ],
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 697,
                    y: -19,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 540,
                    y: 220,
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.5,
                    y: 0.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0,
                    y: 0,
                },
            ],
        },
        {
            name: 'FS Win Frame Right',
            type: AssetType.SPRITE,
            visible: false,
            src: `FREESPINSFrame.png`,
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground, ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        ensureFreeSpinsCounterTexts(element)
                        updateFreeSpinsCounterTexts(element)
                        gsap.killTweensOf(element)
                        element.alpha = 0
                        element.visible = false
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.completeTransitionAnimation],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (isPortrait() || !isFreeSpins()) {
                            return
                        }

                        const element = data.component.componentElement
                        ensureFreeSpinsCounterTexts(element)
                        updateFreeSpinsCounterTexts(element)
                        gsap.killTweensOf(element)
                        element.alpha = 0
                        element.visible = true
                        gsap.to(element, {
                            duration: 0.35,
                            alpha: 1,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.bonusStop],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        gsap.killTweensOf(element)

                        if (!element.visible) {
                            return
                        }

                        gsap.to(element, {
                            duration: 0.35,
                            alpha: 0,
                            onComplete: () => {
                                element.visible = false
                            },
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.stopSpin],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        updateFreeSpinsCounterTexts(element, data.signalData)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsRetrigger, GameSignals.restoreFreeSpins],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        updateFreeSpinsCounterTexts(element)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        const valueText = element.getChildByName(FS_COUNTER_VALUE_CHILD_NAME) as PIXI.Text
                        if (valueText) {
                            valueText.text = ''
                        }
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    event: ComponentEvent.HIDE,
                },
            ],
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1468,
                    y: 274.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 540,
                    y: 700,
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.52,
                    y: 0.52,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0,
                    y: 0,
                },
            ],
        },
    ],
}
